import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Search, Sun, Moon, Languages } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/store/auth";
import { Logo } from "@/components/Logo";
import { NAV } from "@/lib/nav";
import { NotificationsBell } from "./NotificationsBell";
import { GlobalTimerChip } from "@/components/pm/TimeAndRecurrence";
import GlobalPunchPill from "./GlobalPunchPill";
import { ProfileMenu } from "./ProfileMenu";
import { TourButton } from "./TourComponents";
import { usePermission } from "@/hooks/usePermission";
import { Badge } from "@/components/ui/badge";

export function Topbar({ onCollapseToggle }) {
  const { t, i18n } = useTranslation();
  const { theme, toggle } = useTheme();
  const { setLanguage } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const canUsers = usePermission("users", "view");
  const canAudit = usePermission("audit", "view");
  const canViewAdmin = canUsers || canAudit;

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 8);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const visible = NAV.filter((m) => (m.key === "admin" ? canViewAdmin : true));

  const switchLang = async () => {
    const next = i18n.language?.startsWith("hi") ? "en" : "hi";
    await i18n.changeLanguage(next);
    setLanguage(next);
  };

  return (
    <header
      data-testid="topbar"
      className={[
        "fixed top-0 right-0 left-0 lg:left-[264px] z-30 h-16 flex items-center gap-3 px-4 lg:px-6",
        "glass border-b border-[var(--surface-border)]",
        "transition-[left,background,backdrop-filter,box-shadow] duration-200",
        scrolled ? "topbar-scrolled" : "",
      ].join(" ")}
    >
      {/* Mobile / tablet hamburger -> Sheet drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-xl bg-white/5 border border-[var(--surface-border)]"
            data-testid="open-drawer-btn"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[280px] bg-card/95 backdrop-blur-xl border-[var(--surface-border)]">
          <div className="flex items-center gap-3 pt-2 pb-6">
            <Logo size={32} kind="mark" />
            <div>
              <div className="font-heading text-sm font-bold tracking-widest">SHARDA ENERGY OS</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("app.tagline")}</div>
            </div>
          </div>
          <div className="space-y-1">
            {visible.map((m) => {
              const Icon = m.icon;
              const active = m.key === "home" ? loc.pathname === "/" : loc.pathname.startsWith(m.to);
              return (
                <Link
                  key={m.key}
                  to={m.to}
                  data-testid={`drawer-nav-${m.key}`}
                  onClick={() => setOpen(false)}
                  className={[
                    "flex items-center gap-3 rounded-2xl px-3 py-2.5 transition-all",
                    active ? "bg-gradient-primary text-white" : "hover:bg-white/5 text-foreground/85",
                  ].join(" ")}
                >
                  <Icon className="h-5 w-5" />
                  <span className="flex-1 text-sm font-medium">{t(m.labelKey)}</span>
                  {!m.enabled && (
                    <Badge variant="secondary" className="text-[10px] bg-white/10 border-0">P{m.phase}</Badge>
                  )}
                </Link>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      {/* Brand text on mobile only */}
      <div className="lg:hidden flex items-center gap-2">
        <Logo size={28} kind="mark" />
        <span className="font-heading text-sm font-bold tracking-widest">SHARDA ENERGY OS</span>
      </div>

      {/* Phase 23-B -- Cmd+K palette trigger. Clicking dispatches an event
          the GlobalCommandPalette listens for; muscle memory + button click
          both go through the same code path. */}
      <div className="hidden md:flex flex-1 max-w-md mx-3">
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent("open-command-palette"))}
          data-testid="cmdk-palette-trigger"
          className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 hover:border-sky/30 text-xs text-foreground/55 transition-colors"
        >
          <Search className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Search commands, notes, tasks, people…</span>
          <kbd className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/5 border border-white/10">
            ⌘K
          </kbd>
        </button>
      </div>

      <div className="flex-1 lg:flex-none" />

      {/* Language toggle */}
      <button
        onClick={switchLang}
        data-testid="lang-toggle-btn"
        title="Toggle language"
        className="inline-flex items-center gap-1.5 h-10 px-3 rounded-xl bg-white/5 border border-[var(--surface-border)] hover:bg-white/10 text-xs uppercase tracking-widest"
      >
        <Languages className="h-4 w-4" />
        {i18n.language?.startsWith("hi") ? "Hi" : "En"}
      </button>

      {/* Theme toggle */}
      <button
        onClick={toggle}
        data-testid="theme-toggle-btn"
        title="Toggle theme"
        className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-white/5 border border-[var(--surface-border)] hover:bg-white/10"
      >
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>

      <GlobalTimerChip />
      <GlobalPunchPill />
      <NotificationsBell />
      <TourButton />
      <span data-tour-id="topbar-profile" className="contents">
        <ProfileMenu />
      </span>
    </header>
  );
}

export default Topbar;
